import { useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, ExternalLink, FileSearch } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Link } from 'react-router-dom'
import type { ChatCitation } from '../types'

// Friendly label for each source the chat tools can cite. Add new
// entries here when a new tool starts emitting citations from a new
// table - keeps the badge text human-readable instead of showing the
// raw table name.
const SOURCE_LABEL: Record<string, string> = {
  bank_statement: 'Transaction',
  journal_entries: 'Journal entry',
  accounting_entries: 'Ledger entry',
  reconciliations: 'Reconciliation',
  budgets: 'Budget',
  allocation_schedules: 'Schedule',
  invoices: 'Invoice',
  customers: 'Customer',
  customer_payments: 'Payment',
  credit_notes: 'Credit note',
  refunds: 'Refund',
  invoice_write_offs: 'Write-off',
  recurring_invoice_templates: 'Recurring invoice',
  bills: 'Bill',
  vendors: 'Vendor',
  vendor_payments: 'Vendor payment',
  payment_batches: 'Payment batch',
  accruals: 'Accrual',
  prepaid_schedules: 'Prepaid',
  loan_schedules: 'Loan',
  fixed_asset_schedules: 'Fixed asset',
  agents: 'Agent',
  workflows: 'Workflow',
  approval_workflow_instances: 'Approval',
  notifications: 'Notification',
  audit_logs: 'Audit entry',
}

// Two route maps: detail with `:id`, and list-only fallback. When a
// row_id is present and the detail route exists, we link straight to
// the row's detail page (e.g. /invoicing/invoices/123) so the user
// lands on the actual record. Otherwise we fall back to the list page.
const SOURCE_DETAIL_ROUTE: Record<string, (id: string | number) => string> = {
  invoices: (id) => `/invoicing/invoices/${id}`,
  customers: (id) => `/invoicing/customers/${id}/edit`,
  bills: (id) => `/invoicing/bills/${id}`,
  vendors: (id) => `/invoicing/vendors/${id}`,
}

const SOURCE_LIST_ROUTE: Record<string, string> = {
  bank_statement: '/view-statements',
  journal_entries: '/journal-entry',
  accounting_entries: '/view-statements',
  reconciliations: '/reconcile',
  budgets: '/budget-planning',
  allocation_schedules: '/fixed-assets',
  invoices: '/invoicing/invoices',
  customers: '/invoicing/customers',
  customer_payments: '/invoicing/payments',
  credit_notes: '/invoicing/invoices',
  refunds: '/invoicing/payments',
  invoice_write_offs: '/invoicing/invoices',
  recurring_invoice_templates: '/invoicing/recurring',
  bills: '/invoicing/bills',
  vendors: '/invoicing/vendors',
  vendor_payments: '/invoicing/payments',
  payment_batches: '/invoicing/payments',
  accruals: '/accruals',
  prepaid_schedules: '/prepaid',
  loan_schedules: '/loan-amortization',
  fixed_asset_schedules: '/fixed-assets',
  agents: '/agents',
  workflows: '/workflows',
  approval_workflow_instances: '/approvals',
  notifications: '/notifications',
  audit_logs: '/team-management',
}

function defaultHref(c: ChatCitation): string | null {
  // Explicit href on the citation always wins (model can override).
  if (c.href) return c.href
  // Prefer the row-specific detail page so the click takes the user
  // straight to the record, not just the list view.
  if (c.row_id != null && c.row_id !== '') {
    const detail = SOURCE_DETAIL_ROUTE[c.source]
    if (detail) return detail(c.row_id)
  }
  // Otherwise fall back to the list page for that resource.
  return SOURCE_LIST_ROUTE[c.source] ?? null
}

/**
 * Append a `?fromChat=<threadId>` (and optional `&fromMsg=<messageId>`)
 * back-pointer to a citation URL. The destination page renders a
 * BackToChatBanner that reads these params and offers a one-click jump
 * back to the exact thread the user came from - so no "open chat in a
 * new tab to keep your place" workaround is needed.
 *
 * Skips for absolute URLs (external links shouldn't carry our internal
 * trace) and when no threadId is known (e.g. live-streaming turns
 * before the thread id is pinned).
 */
function withChatBacklink(
  href: string,
  threadId: string | null | undefined,
  messageId: string | null | undefined,
): string {
  if (!threadId) return href
  if (/^https?:\/\//i.test(href)) return href
  const sep = href.includes('?') ? '&' : '?'
  const parts = [`fromChat=${encodeURIComponent(threadId)}`]
  if (messageId) parts.push(`fromMsg=${encodeURIComponent(messageId)}`)
  return `${href}${sep}${parts.join('&')}`
}

interface Props {
  citations: ChatCitation[] | null | undefined
  /** Carried into citation URLs as ?fromChat so the destination page can
   *  show a "Back to chat" pill - see BackToChatBanner. */
  threadId?: string | null
  /** Optional: lets the back-link scroll to the exact assistant turn
   *  the user came from (banner currently only re-opens the thread,
   *  but the param is forwarded so a future deep-link is one change). */
  messageId?: string | null
}

export function CitationList({ citations, threadId, messageId }: Props) {
  const [open, setOpen] = useState(false)

  const deduped = useMemo(() => {
    if (!citations) return [] as ChatCitation[]
    const seen = new Set<string>()
    const out: ChatCitation[] = []
    for (const c of citations) {
      const key = `${c.source}:${c.row_id ?? ''}`
      if (seen.has(key)) continue
      seen.add(key)
      out.push(c)
    }
    return out
  }, [citations])

  if (deduped.length === 0) return null

  return (
    <div className="mt-2">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        className="h-6 gap-1.5 px-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        {open ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
        <FileSearch className="size-3" />
        {deduped.length} source{deduped.length === 1 ? '' : 's'}
      </Button>
      {open ? (
        <ul className="mt-1 space-y-0.5 border-l-2 border-border pl-3">
          {deduped.slice(0, 50).map((c, i) => {
            const baseHref = defaultHref(c)
            const href = baseHref ? withChatBacklink(baseHref, threadId, messageId) : null
            const label = c.label || String(c.row_id ?? '')
            const source = SOURCE_LABEL[c.source] || c.source
            const isExternal = href != null && /^https?:\/\//i.test(href)
            const inner = (
              <span className="flex items-center gap-2 text-xs">
                <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {source}
                </span>
                <span className="truncate">{label || `#${c.row_id ?? '-'}`}</span>
                {href ? (
                  <ExternalLink
                    className="size-3 shrink-0 text-muted-foreground/60 transition-colors group-hover:text-primary"
                    aria-hidden
                  />
                ) : null}
              </span>
            )
            return (
              <li key={`${c.source}-${c.row_id ?? i}-${i}`}>
                {href ? (
                  // Primary-color hover + underline make the row read as
                  // a link, not just a visited badge. Block element so
                  // the entire row is the click target, not just the
                  // text - easier to hit on mobile.
                  <Link
                    to={href}
                    target={isExternal ? '_blank' : undefined}
                    rel={isExternal ? 'noreferrer noopener' : undefined}
                    title={`Open ${source.toLowerCase()}${label ? `: ${label}` : ''}`}
                    className="group block rounded px-1 py-0.5 text-muted-foreground transition-colors hover:bg-muted/60 hover:text-primary hover:[&_span:nth-child(2)]:underline"
                  >
                    {inner}
                  </Link>
                ) : (
                  <div className="px-1 py-0.5 text-muted-foreground/70" title="No detail page for this source">
                    {inner}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}
